/**
 * Overdraft protection: Reg E (ATM & one-time debit opt-in) only applies while
 * overdraft protection is ENROLLED (MacKenzie Mott life-ring report 2026-10-16:
 * it stayed offered when the overdraft was declined or revoked; Bill approved
 * 2026-10-16). The server already drops it (pcb-retail-api 3.25.1,
 * CartCommitService.RegEOptInForCore); this keeps the screen in step: when the
 * status moves off "enrolled" the Reg E field is hidden and set to opted out.
 *
 * The feature schema also carries `"enabledWhen":{"key":"odpStatus","equals":"enrolled"}`
 * on regEOptIn (seed 415); this rule holds even before that seed runs.
 * Pure — unit-tested.
 */

export const ODP_STATUS_KEY = 'odpStatus';
export const REG_E_KEY = 'regEOptIn';
export const REG_E_OPT_OUT = 'optOut';

interface SchemaField {
  key: string;
  defaultValue?: unknown;
  enabledWhen?: unknown;
}

interface CompoundSchemaLike {
  fields: ReadonlyArray<SchemaField>;
}

/**
 * The sibling value an `enabledWhen` gate compares against: the stored value,
 * or the field's defaultValue when nothing (undefined or null) is stored yet —
 * the segment control shows its default in that case, so the gate must agree.
 */
export function siblingValue(
  schema: CompoundSchemaLike,
  value: Record<string, unknown>,
  key: string,
): unknown {
  const v = value[key];
  if (v !== undefined && v !== null) return v;
  return schema.fields.find((f) => f.key === key)?.defaultValue;
}

/** True unless the field's `enabledWhen:{key,equals}` gate fails. */
export function fieldGateOpen(
  schema: CompoundSchemaLike,
  value: Record<string, unknown>,
  field: SchemaField,
): boolean {
  const ew = field.enabledWhen as { key?: unknown; equals?: unknown } | undefined;
  if (!ew || typeof ew.key !== 'string') return true;
  return siblingValue(schema, value, ew.key) === ew.equals;
}

/** True when this compound has the overdraft status + Reg E pair. */
function hasOdpPair(schema: CompoundSchemaLike): boolean {
  return schema.fields.some((f) => f.key === ODP_STATUS_KEY) && schema.fields.some((f) => f.key === REG_E_KEY);
}

/** Overdraft status as shown: stored value, else the schema default, else enrolled. */
export function effectiveOdpStatus(schema: CompoundSchemaLike, value: Record<string, unknown>): string {
  const v = siblingValue(schema, value, ODP_STATUS_KEY);
  return typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : 'enrolled';
}

/** True when the Reg E field must be hidden (overdraft not enrolled). */
export function regEHidden(schema: CompoundSchemaLike, value: Record<string, unknown>): boolean {
  return hasOdpPair(schema) && effectiveOdpStatus(schema, value) !== 'enrolled';
}

/**
 * Apply the rule: overdraft not enrolled -> Reg E opted out. Returns the SAME
 * object when nothing changes, so callers can skip a no-op update.
 */
export function applyOdpRegERule(
  schema: CompoundSchemaLike,
  value: Record<string, unknown>,
): Record<string, unknown> {
  if (!regEHidden(schema, value)) return value;
  if (value[REG_E_KEY] === REG_E_OPT_OUT) return value;
  return { ...value, [REG_E_KEY]: REG_E_OPT_OUT };
}
